import { fixtureCityDisplay } from '@/helpers';
import type { Fixture } from '@/models/fixture';
import type { LiveMatch } from '@/models/liveMatch';

export function sortLiveMatchesByRecency(liveMatches: LiveMatch[]): LiveMatch[] {
  return [...liveMatches].sort((a, b) => (b.minute ?? 0) - (a.minute ?? 0));
}

export function excludeFixturesWithLiveMatches(fixtures: Fixture[], liveMatches: LiveMatch[]): Fixture[] {
  const liveMatchIds = new Set(liveMatches.map((m) => m.matchId));
  return fixtures.filter((f) => !f.matchId || !liveMatchIds.has(f.matchId));
}

/** Meta line for scheduled fixtures, e.g. `Sat 14 Jun, 18:00 · ITV1 · Dallas`. */
export function formatFixtureMetaLine(
  kickoff: string,
  fixture: Pick<Fixture, 'ukBroadcaster' | 'cityName' | 'location'>,
  options?: { omitCity?: boolean },
): string {
  const parts = [kickoff];
  const broadcaster = fixture.ukBroadcaster?.trim();
  if (broadcaster) parts.push(broadcaster);
  if (!options?.omitCity) {
    const city = fixtureCityDisplay(fixture);
    if (city) parts.push(city);
  }
  return parts.join(' · ');
}

/** Bottom meta line for live rows, e.g. `Live · ITV1 · Los Angeles`. */
export function formatLiveMetaLine(cityName?: string | null, ukBroadcaster?: string | null): string {
  const parts = ['Live'];
  const broadcaster = ukBroadcaster?.trim();
  if (broadcaster) parts.push(broadcaster);
  const city = cityName?.trim();
  if (city) parts.push(city);
  return parts.join(' · ');
}

export function formatLiveMinute(minute?: number | null, minuteExtra?: number | null): string {
  if (minute == null) return '';
  if (minuteExtra != null && minuteExtra > 0) {
    return `${minute}+${minuteExtra}′`;
  }
  return `${minute}′`;
}

export function formatLiveStatusBadge(
  liveMatch: Pick<LiveMatch, 'status' | 'statusShort' | 'minute' | 'minuteExtra'>,
): string {
  if (liveMatch.status === 'ht') return 'HT';
  if (liveMatch.status === 'ft') return 'FT';
  if (liveMatch.status === 'pen') return 'Pens';
  if (liveMatch.status === 'et') {
    const m = formatLiveMinute(liveMatch.minute, liveMatch.minuteExtra);
    return m ? `ET ${m}` : 'ET';
  }
  if (liveMatch.status === 'live') {
    const m = formatLiveMinute(liveMatch.minute, liveMatch.minuteExtra);
    return m || 'LIVE';
  }
  return liveMatch.statusShort;
}

export function formatLiveEventMinute(minute: number, minuteExtra?: number | null): string {
  if (minuteExtra != null && minuteExtra > 0) {
    return `${minute}+${minuteExtra}′`;
  }
  return `${minute}′`;
}
